// src/components/GraphViewVis.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import { Network } from 'vis-network/standalone/esm/vis-network';
import { USE_MOCK_DATA, mockGraphData, mockNotes } from '../utils/mockData';
import { apiFetch } from './apiFetch';

const GRAPH_URL = "https://refinery-graph-service-fed6gvbmgzdvbrf0.australiasoutheast-01.azurewebsites.net/graph_data";
const NOTES_URL = "https://refinery-note-service-e3btdjbccsbdb5af.australiasoutheast-01.azurewebsites.net/notes";

const normalizeGraph = (data) => {
  if (!data) return { nodes: [], edges: [] };
  if (Array.isArray(data)) {
    const nodes = [];
    const edges = [];
    const seen = new Set();
    data.forEach(graph => {
      (graph.nodes || []).forEach(node => {
        const key = node.entity_name || node.id || node.label;
        if (key && !seen.has(key)) {
          nodes.push(node);
          seen.add(key);
        }
      });
      (graph.edges || []).forEach(edge => edges.push(edge));
    });
    return { nodes, edges };
  }
  return data;
};

const mapCategory = (type = "") => {
  const normalized = type.toLowerCase();
  if (normalized.includes("person")) return "people";
  if (normalized.includes("project") || normalized.includes("meeting")) return "projects";
  if (normalized.includes("place") || normalized.includes("location")) return "places";
  return "ideas";
};

const strengthThresholds = {
  all: 1,
  strong: 2,
  very: 3,
};

const svgToDataUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

const iconSvgs = {
  person: svgToDataUrl(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
      '<circle cx="12" cy="8" r="4" fill="#f2edf9" stroke="#492099" stroke-width="1.8"/>' +
      '<path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" fill="#f2edf9" stroke="#492099" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>'
  ),
  calendar: svgToDataUrl(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
      '<rect x="3" y="5" width="18" height="16" rx="3" fill="#f2edf9" stroke="#492099" stroke-width="1.8"/>' +
      '<path d="M3 9h18" stroke="#492099" stroke-width="1.8"/>' +
      '<rect x="7" y="12" width="3" height="3" rx="0.8" fill="#492099"/>' +
      '<rect x="12" y="12" width="3" height="3" rx="0.8" fill="#492099"/>' +
      '<rect x="7" y="16" width="3" height="3" rx="0.8" fill="#492099"/>' +
    '</svg>'
  ),
  place: svgToDataUrl(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
      '<path d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7z" fill="#f2edf9" stroke="#492099" stroke-width="1.8"/>' +
      '<circle cx="12" cy="9" r="2.5" fill="#492099"/>' +
    '</svg>'
  ),
  project: svgToDataUrl(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
      '<rect x="3" y="8" width="18" height="12" rx="2" fill="#f2edf9" stroke="#492099" stroke-width="1.8"/>' +
      '<path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" fill="#f2edf9" stroke="#492099" stroke-width="1.8"/>' +
      '<path d="M3 12h18" stroke="#492099" stroke-width="1.8"/>' +
    '</svg>'
  ),
  idea: svgToDataUrl(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">' +
      '<path d="M12 3a6 6 0 0 0-3 11v2h6v-2a6 6 0 0 0-3-11z" fill="#f2edf9" stroke="#492099" stroke-width="1.8"/>' +
      '<path d="M9 19h6M10 22h4" stroke="#492099" stroke-width="1.8" stroke-linecap="round"/>' +
    '</svg>'
  ),
};

const getNodeIcon = (type = "") => {
  const normalized = type.toLowerCase();
  if (normalized.includes("person")) return iconSvgs.person;
  if (normalized.includes("meeting") || normalized.includes("event") || normalized.includes("calendar")) return iconSvgs.calendar;
  if (normalized.includes("place") || normalized.includes("location")) return iconSvgs.place;
  if (normalized.includes("project") || normalized.includes("work")) return iconSvgs.project;
  return iconSvgs.idea;
};

const wrapLabel = (value, maxCharsPerLine = 16) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const words = text.split(/\s+/);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    if (!word) return;
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxCharsPerLine) {
      current = candidate;
      return;
    }

    if (current) {
      lines.push(current);
      current = "";
    }

    if (word.length <= maxCharsPerLine) {
      current = word;
      return;
    }

    // Split very long single tokens across multiple lines.
    for (let i = 0; i < word.length; i += maxCharsPerLine) {
      lines.push(word.slice(i, i + maxCharsPerLine));
    }
  });

  if (current) lines.push(current);
  return lines.join("\n");
};

const extractNoteText = (note) => {
  if (!note?.content) return "";
  try {
    const parsed = JSON.parse(note.content);
    return (parsed.blocks || []).map(block => block.text).join(" ");
  } catch (error) {
    return "";
  }
};

const getNoteSnippet = (text, query) => {
  const cleanText = text.replace(/\s+/g, " ").trim();
  if (!cleanText) return "";
  if (!query) return cleanText.slice(0, 120);
  const lower = cleanText.toLowerCase();
  const q = query.toLowerCase();
  const matchIndex = lower.indexOf(q);
  if (matchIndex === -1) return cleanText.slice(0, 120);
  const start = Math.max(0, matchIndex - 40);
  const end = Math.min(cleanText.length, matchIndex + q.length + 40);
  const prefix = start > 0 ? "..." : "";
  const suffix = end < cleanText.length ? "..." : "";
  return `${prefix}${cleanText.slice(start, end)}${suffix}`;
};

const buildEntityDescription = (node, connections = 0, noteCount = 0) => {
  if (!node) return "";
  if (node.description) return node.description;
  const type = node.type || "Concept";
  const normalized = type.toLowerCase();
  if (normalized.includes("person")) {
    return `${node.name} is a person mentioned in ${noteCount} notes with ${connections} related concepts.`;
  }
  if (normalized.includes("meeting") || normalized.includes("event")) {
    return `${node.name} is an event connected to ${connections} related concepts and referenced in ${noteCount} notes.`;
  }
  if (normalized.includes("place") || normalized.includes("location")) {
    return `${node.name} is a place tied to ${connections} concepts and noted in ${noteCount} notes.`;
  }
  if (normalized.includes("project") || normalized.includes("work")) {
    return `${node.name} is a project hub linked to ${connections} related concepts and ${noteCount} notes.`;
  }
  return `${node.name} is a ${type} connected to ${connections} concepts and mentioned in ${noteCount} notes.`;
};

const GraphViewVis = ({
  embedded = false,
  initialCenterConcept = "",
  initialOriginConcept = "",
  onBack,
  onOpenSearch,
  onOpenConcept,
}) => {
  const containerRef = useRef(null);
  const networkRef = useRef(null);
  const baseScaleRef = useRef(1);
  const nodeMapRef = useRef(new Map());
  const adjacencyRef = useRef(new Map());
  const noteMentionCountsRef = useRef(new Map());
  const originConceptRef = useRef("");
  const isMobileViewport = useMemo(() => window.innerWidth <= 900, []);
  const initialScale = isMobileViewport ? 0.88 : 1;
  const location = useLocation();
  const navigate = useNavigate();
  const routeCenter = location.state?.centerConcept || "";
  const routeOrigin = location.state?.originConcept || routeCenter;
  const resolvedInitialCenter = embedded ? (initialCenterConcept || "") : routeCenter;
  const resolvedOriginConcept = embedded
    ? (initialOriginConcept || initialCenterConcept || routeOrigin || "")
    : routeOrigin;
  const [centerConcept, setCenterConcept] = useState(resolvedInitialCenter);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [notes, setNotes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showSecondRing, setShowSecondRing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [strengthFilter, setStrengthFilter] = useState("all");
  const [typeFilters, setTypeFilters] = useState({
    people: true,
    projects: true,
    places: true,
    ideas: true,
  });
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [activeNeighborType, setActiveNeighborType] = useState(null);
  const [controlsMenuOpen, setControlsMenuOpen] = useState(false);
  const [mobileHeaderMenuOpen, setMobileHeaderMenuOpen] = useState(false);

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setGraphData(normalizeGraph(mockGraphData));
      return;
    }
    const token = localStorage.getItem("authToken");
    if (!token) return;
    apiFetch(GRAPH_URL, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setGraphData(normalizeGraph(data)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setNotes(mockNotes);
      return;
    }
    const token = localStorage.getItem("authToken");
    if (!token) return;
    apiFetch(NOTES_URL, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setNotes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const normalized = useMemo(() => {
    const nodes = (graphData.nodes || []).map((node, index) => ({
      id: node.entity_name || node.id || node.label || `node-${index}`,
      name: node.entity_name || node.label || node.id || "Untitled",
      type: node.entity_type || node.entityType || "Idea",
      description: node.description || node.summary || "",
    }));
    const edges = (graphData.edges || []).map((edge, index) => ({
      id: edge.id || `edge-${index}`,
      from: edge.source || edge.from,
      to: edge.target || edge.to,
      label: edge.label || edge.relationship || edge.relation || edge.type,
    })).filter(edge => edge.from && edge.to);
    return { nodes, edges };
  }, [graphData]);

  const nodeMap = useMemo(() => {
    const map = new Map();
    normalized.nodes.forEach(node => map.set(node.id, node));
    return map;
  }, [normalized.nodes]);

  useEffect(() => {
    if (embedded) return;
    if (!centerConcept) {
      navigate('/app', { replace: true });
    }
  }, [embedded, centerConcept, navigate]);

  useEffect(() => {
    if (embedded) return;
    if (!centerConcept) return;
    if (nodeMap.size && !nodeMap.has(centerConcept)) {
      navigate('/app', { replace: true });
    }
  }, [embedded, centerConcept, nodeMap, navigate]);

  useEffect(() => {
    if (!embedded) return;
    setCenterConcept(initialCenterConcept || "");
  }, [embedded, initialCenterConcept]);

  const adjacency = useMemo(() => {
    const map = new Map();
    normalized.nodes.forEach(node => map.set(node.id, new Map()));
    normalized.edges.forEach(edge => {
      if (!map.has(edge.from)) map.set(edge.from, new Map());
      if (!map.has(edge.to)) map.set(edge.to, new Map());
      const fromMap = map.get(edge.from);
      const toMap = map.get(edge.to);
      fromMap.set(edge.to, (fromMap.get(edge.to) || 0) + 1);
      toMap.set(edge.from, (toMap.get(edge.from) || 0) + 1);
    });
    return map;
  }, [normalized]);

  const edgeLabelMap = useMemo(() => {
    const map = new Map();
    (normalized.edges || []).forEach(edge => {
      if (!edge.from || !edge.to) return;
      const label = edge.label || edge.relationship || edge.relation || edge.type || "related";
      map.set(`${edge.from}__${edge.to}`, label);
      map.set(`${edge.to}__${edge.from}`, label);
    });
    return map;
  }, [normalized.edges]);

  const noteMentionCounts = useMemo(() => {
    const counts = new Map();
    if (!notes.length) return counts;
    const noteTexts = notes.map(note => {
      let body = "";
      try {
        const parsed = JSON.parse(note.content);
        body = (parsed.blocks || []).map(block => block.text).join(" ");
      } catch (error) {
        body = "";
      }
      return `${note.title || ""} ${body}`.toLowerCase();
    });
    normalized.nodes.forEach(node => {
      const key = node.name.toLowerCase();
      let count = 0;
      noteTexts.forEach(text => {
        if (text.includes(key)) count += 1;
      });
      counts.set(node.id, count);
    });
    return counts;
  }, [notes, normalized.nodes]);

  const selectedConceptNotes = useMemo(() => {
    if (!selectedSummary?.name) return [];
    const query = selectedSummary.name.toLowerCase();
    if (!query) return [];
    return notes
      .map(note => {
        const text = `${note.title || ""} ${extractNoteText(note)}`.trim();
        return { ...note, __text: text, __lower: text.toLowerCase() };
      })
      .filter(note => note.__lower.includes(query))
      .map(note => ({
        id: note.id,
        title: note.title || "Untitled note",
        snippet: getNoteSnippet(note.__text, selectedSummary.name),
      }))
      .slice(0, 6);
  }, [notes, selectedSummary]);

  useEffect(() => {
    nodeMapRef.current = nodeMap;
  }, [nodeMap]);

  useEffect(() => {
    adjacencyRef.current = adjacency;
  }, [adjacency]);

  useEffect(() => {
    noteMentionCountsRef.current = noteMentionCounts;
  }, [noteMentionCounts]);

  useEffect(() => {
    originConceptRef.current = resolvedOriginConcept;
  }, [resolvedOriginConcept]);

  const buildNeighborList = React.useCallback((nodeId) => {
    if (!nodeId) return [];
    const neighbors = adjacency.get(nodeId);
    if (!neighbors) return [];
    const items = [];
    neighbors.forEach((strength, id) => {
      const node = nodeMap.get(id);
      if (!node) return;
      items.push({ id: node.id, name: node.name, strength, type: node.type });
    });
    return items.sort((a, b) => (b.strength - a.strength) || a.name.localeCompare(b.name));
  }, [adjacency, nodeMap]);

  const layoutData = useMemo(() => {
    if (!centerConcept || !nodeMap.has(centerConcept)) {
      return { nodes: [], edges: [], overflowCount: 0 };
    }
    const centerNode = nodeMap.get(centerConcept);
    const centerNeighbors = adjacency.get(centerNode.id) || new Map();
    const ringOneCandidates = Array.from(centerNeighbors.entries()).map(([id, strength]) => {
      const node = nodeMap.get(id);
      return {
        node,
        strength,
        degree: adjacency.get(id)?.size || 0,
      };
    });
    const strengthMin = strengthThresholds[strengthFilter] || 1;
    const filteredRingOne = ringOneCandidates
      .filter(candidate => candidate.node && typeFilters[mapCategory(candidate.node.type)])
      .filter(candidate => candidate.strength >= strengthMin)
      .sort((a, b) => (b.strength - a.strength) || (b.degree - a.degree));
    const ringOne = filteredRingOne.slice(0, 8);
    const overflowCount = Math.max(filteredRingOne.length - ringOne.length, 0);

    const ringOneIds = new Set(ringOne.map(item => item.node.id));

    const ringTwo = [];
    if (showSecondRing) {
      const secondMap = new Map();
      ringOne.forEach(item => {
        const neighbors = adjacency.get(item.node.id) || new Map();
        neighbors.forEach((strength, id) => {
          if (id === centerNode.id || ringOneIds.has(id)) return;
          const node = nodeMap.get(id);
          if (!node) return;
          if (!typeFilters[mapCategory(node.type)]) return;
          if (strength < strengthMin) return;
          const prev = secondMap.get(id) || { node, strength: 0, degree: adjacency.get(id)?.size || 0 };
          prev.strength += strength;
          secondMap.set(id, prev);
        });
      });
      ringTwo.push(...Array.from(secondMap.values())
        .sort((a, b) => (b.strength - a.strength) || (b.degree - a.degree))
        .slice(0, 14));
    }

    const layoutNodes = [];
    const layoutEdges = [];

    layoutNodes.push({
      id: centerNode.id,
      label: wrapLabel(centerNode.name, 18),
      image: getNodeIcon(centerNode.type),
      shape: 'image',
      font: { color: '#2b2140', size: 14, face: 'Roboto, sans-serif', align: 'bottom', vadjust: -8 },
      size: 28,
      fixed: { x: true, y: true },
      shadow: { enabled: true, size: 14, color: 'rgba(73, 32, 153, 0.2)' },
      x: 0,
      y: 0,
    });

    const placeRing = (items, radius) => {
      const count = items.length || 1;
      const angleStep = (Math.PI * 2) / count;
      return items.map((item, index) => {
        const angle = angleStep * index - Math.PI / 2;
        return {
          ...item,
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius,
        };
      });
    };

    const placedRingOne = placeRing(ringOne, 220);
    const placedRingTwo = placeRing(ringTwo, 340);

    placedRingOne.forEach(item => {
      layoutNodes.push({
        id: item.node.id,
        label: wrapLabel(item.node.name, 16),
        image: getNodeIcon(item.node.type),
        shape: 'image',
        font: { color: '#3d2c59', size: 12, face: 'Roboto, sans-serif', align: 'bottom', vadjust: -6 },
        size: 18,
        fixed: { x: true, y: true },
        x: item.x,
        y: item.y,
      });
      layoutEdges.push({
        id: `edge-${centerNode.id}-${item.node.id}`,
        from: centerNode.id,
        to: item.node.id,
        width: Math.min(3, 0.6 + item.strength * 0.5),
        color: { color: '#e2dbf3', highlight: '#7a4fc3' },
        label: edgeLabelMap.get(`${centerNode.id}__${item.node.id}`) || "related",
        font: { size: 10, color: '#6b5a8a', strokeWidth: 4, strokeColor: '#faf9fd' },
      });
    });

    placedRingTwo.forEach(item => {
      layoutNodes.push({
        id: item.node.id,
        label: wrapLabel(item.node.name, 14),
        image: getNodeIcon(item.node.type),
        shape: 'image',
        font: { color: '#5a4a7b', size: 11, face: 'Roboto, sans-serif', align: 'bottom', vadjust: -4 },
        size: 12,
        fixed: { x: true, y: true },
        x: item.x,
        y: item.y,
      });
    });

    if (selectedNode && activeNeighborType) {
      const anchor = layoutNodes.find(node => node.id === selectedNode.id);
      if (anchor) {
        const existingIds = new Set(layoutNodes.map(node => node.id));
        const haloNeighbors = buildNeighborList(selectedNode.id)
          .filter(neighbor => !existingIds.has(neighbor.id))
          .filter(neighbor => mapCategory(neighbor.type) === activeNeighborType)
          .slice(0, 6);
        if (haloNeighbors.length) {
          const haloRadius = 110;
          const angleStep = (Math.PI * 2) / haloNeighbors.length;
          haloNeighbors.forEach((neighbor, index) => {
            const angle = angleStep * index - Math.PI / 2;
            const x = anchor.x + Math.cos(angle) * haloRadius;
            const y = anchor.y + Math.sin(angle) * haloRadius;
            layoutNodes.push({
              id: neighbor.id,
              label: wrapLabel(neighbor.name, 14),
              image: getNodeIcon(nodeMap.get(neighbor.id)?.type || "Idea"),
              shape: 'image',
              font: { color: '#5a4a7b', size: 10, face: 'Roboto, sans-serif', align: 'bottom', vadjust: -4 },
              size: 16,
              fixed: { x: true, y: true },
              x,
              y,
            });
            layoutEdges.push({
              id: `halo-${selectedNode.id}-${neighbor.id}`,
              from: selectedNode.id,
              to: neighbor.id,
              width: 1.2,
              color: { color: '#e5ddf6', highlight: '#7a4fc3' },
              label: edgeLabelMap.get(`${selectedNode.id}__${neighbor.id}`) || "related",
              font: { size: 9, color: '#6b5a8a', strokeWidth: 4, strokeColor: '#faf9fd' },
            });
          });
        }
      }
    }

    const includedIds = new Set(layoutNodes.map(node => node.id));
    normalized.edges.forEach(edge => {
      if (!includedIds.has(edge.from) || !includedIds.has(edge.to)) return;
      if (layoutEdges.some(existing => (existing.from === edge.from && existing.to === edge.to) || (existing.from === edge.to && existing.to === edge.from))) {
        return;
      }
      const strength = adjacency.get(edge.from)?.get(edge.to) || 1;
      layoutEdges.push({
        id: edge.id,
        from: edge.from,
        to: edge.to,
        width: Math.min(3, 0.6 + strength * 0.5),
        color: { color: '#ece7f6', highlight: '#7a4fc3' },
        label: edgeLabelMap.get(`${edge.from}__${edge.to}`) || "related",
        font: { size: 10, color: '#6b5a8a', strokeWidth: 4, strokeColor: '#faf9fd' },
      });
    });

    return { nodes: layoutNodes, edges: layoutEdges, overflowCount };
  }, [centerConcept, nodeMap, adjacency, showSecondRing, strengthFilter, typeFilters, normalized.edges, edgeLabelMap, selectedNode, activeNeighborType, buildNeighborList]);

  const neighborTypeSummary = useMemo(() => {
    if (!selectedSummary?.neighbors?.length) return [];
    const counts = new Map();
    selectedSummary.neighbors.forEach(neighbor => {
      const category = mapCategory(neighbor.type);
      counts.set(category, (counts.get(category) || 0) + 1);
    });
    const labels = {
      people: "People",
      projects: "Projects",
      places: "Places",
      ideas: "Ideas",
    };
    return Array.from(counts.entries())
      .map(([type, count]) => ({
        type,
        count,
        label: labels[type] || type,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [selectedSummary]);

  const handleNetworkClick = React.useCallback((params) => {
    if (!params.nodes.length) {
      setSelectedNode(null);
      setSelectedSummary(null);
      setSummaryExpanded(false);
      setActiveNeighborType(null);
      return;
    }
    const nodeId = params.nodes[0];
    const node = nodeMapRef.current.get(nodeId) || null;
    setSelectedNode(node);
    setSummaryExpanded(false);
    setActiveNeighborType(null);
    if (node) {
      const adjacencyMap = adjacencyRef.current;
      const neighborsMap = adjacencyMap.get(node.id);
      const connections = neighborsMap?.size || 0;
      const noteCount = noteMentionCountsRef.current.get(node.id) || 0;
      const neighbors = [];
      if (neighborsMap) {
        neighborsMap.forEach((strength, id) => {
          const neighborNode = nodeMapRef.current.get(id);
          if (!neighborNode) return;
          neighbors.push({ id: neighborNode.id, name: neighborNode.name, strength, type: neighborNode.type });
        });
      }
      neighbors.sort((a, b) => (b.strength - a.strength) || a.name.localeCompare(b.name));
      setSelectedSummary({
        id: node.id,
        name: node.name,
        type: node.type,
        connections,
        noteCount,
        description: buildEntityDescription(node, connections, noteCount),
        neighbors,
      });
    }
  }, []);

  const handleNetworkHold = React.useCallback((params) => {
    if (!params.nodes.length) return;
    const nodeId = params.nodes[0];
    const node = nodeMapRef.current.get(nodeId);
    if (!node) return;
    setSelectedNode(null);
    setSelectedSummary(null);
    if (embedded && onOpenConcept) {
      onOpenConcept(node.name);
      return;
    }
    navigate('/app', { state: { conceptName: node.name } });
  }, [embedded, navigate, onOpenConcept]);

  const handleNetworkDoubleClick = React.useCallback(() => {
    const origin = originConceptRef.current;
    if (!origin) return;
    setCenterConcept(origin);
    setShowSecondRing(false);
  }, []);

  const handleNetworkDragEnd = React.useCallback(() => {
    networkRef.current?.moveTo({
      position: { x: 0, y: 0 },
      scale: baseScaleRef.current,
      animation: { duration: 220, easingFunction: 'easeOutQuad' },
    });
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const data = { nodes: layoutData.nodes, edges: layoutData.edges };
    const options = {
      autoResize: false,
      physics: false,
      layout: { improvedLayout: false },
      nodes: { borderWidth: 0 },
      edges: { smooth: false, font: { size: 10, color: '#6b5a8a', strokeWidth: 4, strokeColor: '#faf9fd' } },
      interaction: {
        dragNodes: false,
        dragView: true,
        zoomView: true,
        hover: true,
        tooltipDelay: 150,
      },
    };
    if (networkRef.current) {
      networkRef.current.setData(data);
      networkRef.current.moveTo({
        position: { x: 0, y: 0 },
        scale: baseScaleRef.current,
        animation: false,
      });
    } else {
      networkRef.current = new Network(containerRef.current, data, options);
      networkRef.current.moveTo({
        position: { x: 0, y: 0 },
        scale: initialScale,
        animation: false,
      });
      baseScaleRef.current = initialScale;
    }
    networkRef.current.off('click', handleNetworkClick);
    networkRef.current.off('hold', handleNetworkHold);
    networkRef.current.off('doubleClick', handleNetworkDoubleClick);
    networkRef.current.off('dragEnd', handleNetworkDragEnd);
    networkRef.current.on('click', handleNetworkClick);
    networkRef.current.on('hold', handleNetworkHold);
    networkRef.current.on('doubleClick', handleNetworkDoubleClick);
    networkRef.current.on('dragEnd', handleNetworkDragEnd);
  }, [layoutData, handleNetworkClick, handleNetworkHold, handleNetworkDoubleClick, handleNetworkDragEnd, initialScale]);

  useEffect(() => {
    if (!networkRef.current) return;
    if (!selectedNode) return;
    const updatedEdges = layoutData.edges.map(edge => {
      const isConnected = edge.from === selectedNode.id || edge.to === selectedNode.id;
      return {
        ...edge,
        width: isConnected ? edge.width + 1.4 : edge.width,
      };
    });
    networkRef.current.setData({ nodes: layoutData.nodes, edges: updatedEdges });
  }, [selectedNode, layoutData]);

  useEffect(() => {
    if (!mobileHeaderMenuOpen) return;
    const handleOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.nr-graph-brand-menu-wrap')) return;
      setMobileHeaderMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [mobileHeaderMenuOpen]);

  const showMobileBanner = !embedded && isMobileViewport;
  const showBackBanner = !embedded && !isMobileViewport;
  const showGraphActionBar = !embedded && !isMobileViewport;
  const showMobileBottomBar = !embedded && isMobileViewport;
  const showDesktopOverflowMenu = embedded;
  const panelBottomOffset = showGraphActionBar ? 84 : (showMobileBottomBar ? 98 : 16);
  const canToggleSecondRing = showSecondRing || layoutData.overflowCount > 0;

  return (
    <div style={{ height: embedded ? '100%' : '100vh', background: 'linear-gradient(180deg, #f7f4fb 0%, #ffffff 56%, #f2eef9 100%)', display: 'flex', flexDirection: 'column' }}>
      {showBackBanner && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '12px 16px',
          background: 'rgba(255, 255, 255, 0.92)',
          borderBottom: '1px solid rgba(73, 32, 153, 0.12)',
          boxShadow: '0 6px 18px rgba(73, 32, 153, 0.06)',
          backdropFilter: 'saturate(140%) blur(6px)'
        }}>
          <IconButton
            onClick={() => navigate(-1)}
            size="small"
            sx={{
              mr: 0.5,
              color: '#492099',
              backgroundColor: 'rgba(73, 32, 153, 0.08)',
              '&:hover': { backgroundColor: 'rgba(73, 32, 153, 0.14)' }
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Button
            onClick={() => navigate(-1)}
            size="small"
            sx={{
              textTransform: 'none',
              color: '#2b2140',
              fontSize: '15px',
              fontWeight: 600
            }}
          >
            Back to {resolvedOriginConcept || 'concept'}
          </Button>
        </div>
      )}
      {showMobileBanner && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px 8px',
          background: 'transparent',
          borderBottom: 'none',
          boxShadow: 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#492099', fontSize: 20, fontWeight: 600 }}>
            <img src="/logo2-removebg-preview.png" alt="NoteRefinery logo" style={{ width: 42, height: 42 }} />
            <span style={{ lineHeight: 1, marginTop: 8 }}>NoteRefinery</span>
          </div>
          <div className="nr-graph-brand-menu-wrap" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setMobileHeaderMenuOpen((open) => !open)}
              aria-label="Open menu"
              aria-haspopup="menu"
              aria-expanded={mobileHeaderMenuOpen}
              style={{
                border: '1px solid rgba(73, 32, 153, 0.2)',
                background: 'rgba(255, 255, 255, 0.72)',
                color: '#492099',
                borderRadius: 999,
                width: 34,
                height: 34,
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
            >
              <MenuIcon sx={{ fontSize: 18 }} />
            </button>
            {mobileHeaderMenuOpen && (
              <div role="menu" aria-label="Account menu" style={{ position: 'absolute', top: 40, right: 0, zIndex: 20, minWidth: 150, border: '1px solid rgba(73, 32, 153, 0.2)', borderRadius: 10, background: '#fff', boxShadow: '0 10px 20px rgba(73, 32, 153, 0.14)', overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => {
                    setMobileHeaderMenuOpen(false);
                    localStorage.removeItem('authToken');
                    navigate('/signin');
                  }}
                  style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', padding: '10px 12px', color: '#4b3c67', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ flex: 1, position: 'relative', background: 'transparent' }}>
        <div ref={containerRef} style={{ height: '100%', width: '100%', background: 'transparent' }} />
        {showMobileBanner && (
          <button
            type="button"
            onClick={() => {
              if (resolvedOriginConcept) {
                navigate('/app', { state: { conceptName: resolvedOriginConcept } });
                return;
              }
              navigate('/app');
            }}
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 6,
              border: '1px solid rgba(73, 32, 153, 0.2)',
              borderRadius: 999,
              background: 'rgba(255, 255, 255, 0.9)',
              color: '#492099',
              fontSize: 12,
              fontWeight: 700,
              minHeight: 34,
              padding: '0 11px',
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}
          >
            Back to concept
          </button>
        )}

        {selectedSummary && (
          <div style={{
            position: 'absolute',
            left: 16,
            right: 16,
            bottom: panelBottomOffset,
            background: '#ffffff',
            borderRadius: 18,
            border: '1px solid #ece7f6',
            boxShadow: '0 18px 40px rgba(73, 32, 153, 0.12)',
            padding: '14px 16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#2b2140' }}>{selectedSummary.name}</div>
              <button
                type="button"
                onClick={() => setSummaryExpanded(value => !value)}
                style={{
                  border: 'none',
                  background: 'none',
                  color: '#6b5a8a',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {summaryExpanded ? 'Less info' : 'More info'}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{
                background: '#f2edf9',
                color: '#492099',
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 600,
              }}>
                {selectedSummary.type}
              </span>
              <span style={{ fontSize: 12, color: '#6b5a8a' }}>
                Appears in {selectedSummary.noteCount} notes
              </span>
              {summaryExpanded && (
                <span style={{ fontSize: 12, color: '#6b5a8a' }}>
                  {selectedSummary.connections} connections
                </span>
              )}
            </div>
            {summaryExpanded && selectedSummary.neighbors && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#8a7ea6', fontWeight: 700 }}>
                  Neighbor types
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {neighborTypeSummary.map(({ type, label, count }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setActiveNeighborType(prev => (prev === type ? null : type));
                      }}
                      style={{
                        border: activeNeighborType === type ? '1px solid #492099' : '1px dashed #d6cdec',
                        background: activeNeighborType === type ? '#f2edf9' : '#fbf9ff',
                        color: activeNeighborType === type ? '#492099' : '#6b5a8a',
                        borderRadius: 999,
                        padding: '4px 9px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {label} ({count})
                    </button>
                  ))}
                  {neighborTypeSummary.length === 0 && (
                    <span style={{ fontSize: 12, color: '#6b5a8a' }}>No linked concepts yet.</span>
                  )}
                </div>
                {neighborTypeSummary.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#6b5a8a' }}>
                    {activeNeighborType
                      ? `Showing ${neighborTypeSummary.find(item => item.type === activeNeighborType)?.label || 'neighbors'} on the map.`
                      : 'Tap a type to reveal matching neighbors on the map.'}
                  </div>
                )}
              </div>
            )}
            {summaryExpanded && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#8a7ea6', fontWeight: 700 }}>
                  Notes mentioning this concept
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                  {selectedConceptNotes.map(note => (
                    <div key={note.id} style={{ border: '1px solid #efe9f8', borderRadius: 14, padding: '10px 12px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#2b2140' }}>{note.title}</div>
                      <div style={{ fontSize: 12, color: '#6b5a8a', marginTop: 6 }}>{note.snippet}</div>
                    </div>
                  ))}
                  {selectedConceptNotes.length === 0 && (
                    <span style={{ fontSize: 12, color: '#6b5a8a' }}>No notes mention this concept yet.</span>
                  )}
                </div>
              </div>
            )}
            {summaryExpanded && (
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    if (embedded && onOpenSearch) {
                      onOpenSearch(selectedSummary.name);
                      return;
                    }
                    navigate('/app', { state: { searchQuery: selectedSummary.name } });
                  }}
                  style={{
                    border: 'none',
                    background: '#492099',
                    color: '#fff',
                    borderRadius: 12,
                    padding: '9px 14px',
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: '0.01em',
                  }}
                >
                  View notes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (embedded && onOpenConcept) {
                      onOpenConcept(selectedSummary.name);
                      return;
                    }
                    navigate('/app', { state: { conceptName: selectedSummary.name } });
                  }}
                  style={{
                    border: '1px dashed #b7a6de',
                    background: '#fbf9ff',
                    color: '#5c4a7a',
                    borderRadius: 12,
                    padding: '9px 14px',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  See full concept
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCenterConcept(selectedSummary.id);
                    setShowSecondRing(false);
                  }}
                  style={{
                    border: '1px solid #c9b9ef',
                    background: '#ffffff',
                    color: '#492099',
                    borderRadius: 12,
                    padding: '9px 14px',
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Re-center graph
                </button>
              </div>
            )}
          </div>
        )}

        {showFilters && (
          <div style={{
            position: 'absolute',
            right: 16,
            bottom: panelBottomOffset,
            background: '#ffffff',
            borderRadius: 16,
            border: '1px solid #ece7f6',
            boxShadow: '0 12px 32px rgba(73, 32, 153, 0.12)',
            padding: '12px 14px',
            width: 220,
          }}>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#8a7ea6', fontWeight: 700 }}>
              Filter
            </div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.keys(typeFilters).map(type => (
                <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#2b2140' }}>
                  <input
                    type="checkbox"
                    checked={typeFilters[type]}
                    onChange={() => setTypeFilters(prev => ({ ...prev, [type]: !prev[type] }))}
                  />
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </label>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#8a7ea6', fontWeight: 700 }}>
                Strength
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {Object.keys(strengthThresholds).map(key => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStrengthFilter(key)}
                    style={{
                      border: '1px solid #dcd3ee',
                      background: strengthFilter === key ? '#492099' : '#f7f4fb',
                      color: strengthFilter === key ? '#ffffff' : '#492099',
                      borderRadius: 8,
                      padding: '6px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  >
                    {key}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {showDesktopOverflowMenu && (
          <div style={{ position: 'absolute', right: 16, top: 16, zIndex: 6 }}>
            <IconButton
              onClick={() => setControlsMenuOpen(value => !value)}
              size="small"
              sx={{
                color: '#492099',
                border: '1px solid rgba(73, 32, 153, 0.2)',
                backgroundColor: 'rgba(255, 255, 255, 0.94)',
                boxShadow: '0 10px 18px rgba(73, 32, 153, 0.12)',
                '&:hover': { backgroundColor: '#ffffff' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            {controlsMenuOpen && (
              <div style={{
                marginTop: 8,
                minWidth: 170,
                borderRadius: 12,
                border: '1px solid rgba(73, 32, 153, 0.14)',
                background: '#fff',
                boxShadow: '0 14px 24px rgba(73, 32, 153, 0.16)',
                overflow: 'hidden'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setControlsMenuOpen(false);
                    if (!resolvedOriginConcept) return;
                    setCenterConcept(resolvedOriginConcept);
                    setShowSecondRing(false);
                  }}
                  style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', padding: '10px 12px', color: '#2b2140', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Re-center
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!canToggleSecondRing) return;
                    setControlsMenuOpen(false);
                    setShowSecondRing(value => !value);
                  }}
                  disabled={!canToggleSecondRing}
                  style={{ width: '100%', border: 'none', borderTop: '1px solid rgba(73, 32, 153, 0.1)', background: 'none', textAlign: 'left', padding: '10px 12px', color: canToggleSecondRing ? '#2b2140' : '#aea4c4', fontSize: 13, fontWeight: 600, cursor: canToggleSecondRing ? 'pointer' : 'not-allowed' }}
                >
                  {showSecondRing ? 'Collapse' : `Expand${layoutData.overflowCount ? ` (+${layoutData.overflowCount})` : ''}`}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setControlsMenuOpen(false);
                    setShowFilters(value => !value);
                  }}
                  style={{ width: '100%', border: 'none', borderTop: '1px solid rgba(73, 32, 153, 0.1)', background: 'none', textAlign: 'left', padding: '10px 12px', color: '#2b2140', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  {showFilters ? 'Hide filters' : 'Filter'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showGraphActionBar && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          background: '#ffffff',
          borderTop: '1px solid #ece7f6',
          display: 'flex',
          justifyContent: 'space-around',
          padding: '12px 16px calc(env(safe-area-inset-bottom) + 12px)',
        }}>
          <button
            type="button"
            onClick={() => {
              if (!resolvedOriginConcept) return;
              setCenterConcept(resolvedOriginConcept);
              setShowSecondRing(false);
            }}
            style={{ border: 'none', background: 'none', color: '#492099', fontWeight: 600 }}
          >
            Re-center
          </button>
          <button
            type="button"
            onClick={() => setShowSecondRing(value => !value)}
            disabled={!canToggleSecondRing}
            style={{
              border: 'none',
              background: 'none',
              color: canToggleSecondRing ? '#492099' : '#b9a9d6',
              fontWeight: 600,
            }}
          >
            {showSecondRing ? 'Collapse' : `Expand${layoutData.overflowCount ? ` (+${layoutData.overflowCount})` : ''}`}
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(value => !value)}
            style={{ border: 'none', background: 'none', color: '#492099', fontWeight: 600 }}
          >
            Filter
          </button>
        </div>
      )}
      {showMobileBottomBar && (
        <nav
          aria-label="Primary"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '10px 16px calc(env(safe-area-inset-bottom) + 12px)',
            display: 'flex',
            justifyContent: 'space-around',
            background: 'rgba(255, 255, 255, 0.9)',
            borderTop: '1px solid rgba(18, 50, 53, 0.08)',
            backdropFilter: 'blur(10px)',
            zIndex: 10
          }}
        >
          <button
            type="button"
            onClick={() => navigate('/app')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#9a90b4', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
          >
            <SearchIcon fontSize="small" />
            <span>Explore</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/app?view=notes')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#9a90b4', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
          >
            <DescriptionOutlinedIcon fontSize="small" />
            <span>Notes</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#9a90b4', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
          >
            <SettingsIcon fontSize="small" />
            <span>Support</span>
          </button>
        </nav>
      )}
    </div>
  );
};

export default GraphViewVis;
